import React from "react";
/**
 * VisibilityTracker - A unified event emitter for tracking element visibility
 * Combines IntersectionObserver (viewport visibility) and Page Visibility API (tab changes)
 * to provide a single interface for monitoring when elements are truly visible to users.
 */

class VisibilityTracker {
  constructor() {
    // Store callbacks for each tracked element
    this.trackedElements = new Map();
    
    // Create a single IntersectionObserver for all elements
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const callbacks = this.trackedElements.get(entry.target);
          if (callbacks) {
            // Update viewport visibility state
            callbacks.isInViewport = entry.isIntersecting;
            // Emit event based on combined visibility
            this.emitVisibilityChange(entry.target, callbacks);
          }
        });
      },
      { threshold: 0.1 } // Consider visible when at least 10% is in viewport
    );

    // Listen for tab visibility changes
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Handle page visibility changes (tab switches)
   */
  handleVisibilityChange() {
    const isTabVisible = !document.hidden;
    
    // Notify all tracked elements about tab visibility change
    this.trackedElements.forEach((callbacks, element) => {
      this.emitVisibilityChange(element, callbacks);
    });
  }

  /**
   * Emit visibility change event based on combined state
   * Element is considered "visible" only if both:
   * 1. In viewport (IntersectionObserver)
   * 2. Tab is active (Page Visibility API)
   */
  emitVisibilityChange(element, callbacks) {
    const isTabVisible = !document.hidden;
    const isFullyVisible = callbacks.isInViewport && isTabVisible;
    
    // Only emit if visibility state actually changed
    if (callbacks.lastVisibleState !== isFullyVisible) {
      callbacks.lastVisibleState = isFullyVisible;
      
      /*console.log(
        `Element visibility changed:`,
        `viewport=${callbacks.isInViewport}`,
        `tab=${isTabVisible}`,
        `final=${isFullyVisible}`
      );*/
      
      // Call the callback with visibility state
      callbacks.onChange(isFullyVisible, {
        isInViewport: callbacks.isInViewport,
        isTabVisible: isTabVisible
      });
    }
  }

  /**
   * Start tracking an element's visibility
   * @param {HTMLElement} element - The element to track
   * @param {Function} onChange - Callback function (isVisible, details) => void
   * @returns {Function} Cleanup function to stop tracking
   */
  track(element, onChange) {
    if (!element) {
      console.warn('Cannot track null element');
      return () => {};
    }

    // Initialize tracking state
    this.trackedElements.set(element, {
      onChange,
      isInViewport: false,
      lastVisibleState: null
    });

    // Start observing
    this.intersectionObserver.observe(element);

    // Perform initial visibility check
    const isInViewport = this.isElementInViewport(element);
    const callbacks = this.trackedElements.get(element);
    callbacks.isInViewport = isInViewport;
    this.emitVisibilityChange(element, callbacks);

    // Return cleanup function
    return () => {
      this.untrack(element);
    };
  }

  /**
   * Stop tracking an element
   * @param {HTMLElement} element - The element to stop tracking
   */
  untrack(element) {
    if (this.trackedElements.has(element)) {
      this.intersectionObserver.unobserve(element);
      this.trackedElements.delete(element);
    }
  }

  /**
   * Check if element is currently in viewport (manual check)
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0
    );
  }

  /**
   * Cleanup all tracking and observers
   */
  destroy() {
    // Untrack all elements
    this.trackedElements.forEach((_, element) => {
      this.intersectionObserver.unobserve(element);
    });
    this.trackedElements.clear();

    // Disconnect observer
    this.intersectionObserver.disconnect();

    // Remove event listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}

// Key-based tracker manager
class VisibilityTrackerManager {
  constructor() {
    this.trackers = new Map();
  }

  /**
   * Get or create a tracker instance by key
   * @param {string} key - Unique identifier for the tracker instance
   * @returns {VisibilityTracker} The tracker instance
   */
  getTracker(key = 'default') {
    if (!this.trackers.has(key)) {
      this.trackers.set(key, new VisibilityTracker());
    }
    return this.trackers.get(key);
  }

  /**
   * Remove a tracker instance by key
   * @param {string} key - Unique identifier for the tracker instance
   */
  removeTracker(key) {
    const tracker = this.trackers.get(key);
    if (tracker) {
      tracker.destroy();
      this.trackers.delete(key);
    }
  }

  /**
   * Get all tracker instances
   * @returns {Map<string, VisibilityTracker>} All tracker instances
   */
  getAllTrackers() {
    return this.trackers;
  }

  /**
   * Destroy all trackers
   */
  destroyAll() {
    this.trackers.forEach(tracker => tracker.destroy());
    this.trackers.clear();
  }
}

// Export manager instance
export const visibilityTrackerManager = new VisibilityTrackerManager();

// Convenience function to get default tracker
export const getDefaultTracker = () => visibilityTrackerManager.getTracker('default');

/**
 * React hook for using a named visibility tracker
 * @param {React.RefObject} elementRef - Ref to the element to track
 * @param {Function} onVisibilityChange - Callback when visibility changes
 * @param {string} trackerKey - Key for the tracker instance (optional, defaults to 'default')
 */
export function useElementVisibility(elementRef, onVisibilityChange, trackerKey = 'default') {
  React.useEffect(() => {
    const element = elementRef.current;
    
    if (!element || !onVisibilityChange) {
      return;
    }

    const tracker = visibilityTrackerManager.getTracker(trackerKey);
    
    // Start tracking and get cleanup function
    const cleanup = tracker.track(element, onVisibilityChange);

    // Return cleanup function
    return cleanup;
  }, [elementRef, onVisibilityChange, trackerKey]);
}

/**
 * Hook for creating and managing a named tracker instance
 * @param {string} trackerKey - Key for the tracker instance
 * @returns {VisibilityTracker} The tracker instance
 */
export function useTracker(trackerKey = 'default') {
  const trackerRef = React.useRef(null);

  React.useEffect(() => {
    trackerRef.current = visibilityTrackerManager.getTracker(trackerKey);
    
    return () => {
      // Optionally remove the tracker when component unmounts
      // Remove this if you want the tracker to persist
      // visibilityTrackerManager.removeTracker(trackerKey);
    };
  }, [trackerKey]);

  return trackerRef;
}